import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DateLocalizationService } from '@app/services/date-localization.service';
import { UserService } from '@app/services/user.service';
import { UserCertificate, UserSession } from '@app/store/models';
import { selectCertificate } from '@app/store/selectors/certificate.selector';
import { selectSession } from '@app/store/selectors/session.selector';
import { GlobalState } from '@app/store/store';
import { I18nService } from '@app/translations/i18n';
import { ProfileCard } from '@infocert/ng-ui-kit/models';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { LocalStorageService } from 'ngx-webstorage';
import { Observable, combineLatest } from 'rxjs';

interface SignsCounter {
  weeklySigns: number;
  totalSigns: number;
}

@UntilDestroy()
@Component({
  selector: 'app-user-details',
  templateUrl: './user-details.component.html',
  styleUrls: ['./user-details.component.scss'],
  standalone: false,
})
export class UserDetailsComponent implements OnInit {
  constructor(
    private store: Store<GlobalState>,
    private translateService: TranslateService,
    private localStorageService: LocalStorageService,
    private userService: UserService,
    private i18nService: I18nService,
    private dateLocalizationService: DateLocalizationService,
    public router: Router
  ) {}

  session: UserSession | undefined;

  automatic: boolean = false;
  isIcssUser: boolean = false;
  isTrissUser: boolean | undefined = false;
  isMulticert: boolean = false;
  weeklySigns: number = 0;
  totalSigns: number = 0;

  userSession$: Observable<UserSession>;
  userCertificate$: Observable<UserCertificate>;

  profiles: ProfileCard[] = [];
  primaryCard: ProfileCard;

  $signsCounter: Observable<SignsCounter>;
  isExpiring: boolean;

  async ngOnInit(): Promise<void> {
    this.userSession$ = this.store.select(selectSession);
    this.userCertificate$ = this.store.select(selectCertificate);
    this.isMulticert = this.localStorageService.retrieve('isMulticert');
    this.$signsCounter = this.userService.$signsCounter.asObservable();
    this.$signsCounter.pipe(untilDestroyed(this)).subscribe(counter => {
      this.weeklySigns = counter?.weeklySigns ?? 0;
      this.totalSigns = counter?.totalSigns ?? 0;
    });
    this.updateDetails();
  }

  go(url) {
    this.router.navigate([url]);
  }

  updateDetails() {
    combineLatest([
      this.userSession$,
      this.userCertificate$,
      this.i18nService.currLanguage$,
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([userSession, certificate, language]) => {
        console.log('subscribe updateDetails', {
          userSession,
          certificate,
          language,
        });
        if (userSession && certificate) {
          const fullName = certificate?.certificateInfo?.subjectDN?.CN;
          let alias = certificate?.aliasCertificate;
          if (userSession.icssUser || userSession.trissUser) {
            alias = certificate.email;
          }
          let certType = '';

          switch (certificate.certificateType) {
            case 'AUTOMATIC_SIGN':
              certType = 'COMMON.AUTOMATIC_SIGN';
              break;
            case 'REMOTE_SIGN':
              certType = 'COMMON.REMOTE_SIGN';
              break;
            case 'NOT_VALID':
              if (userSession.trissUser && userSession.automatic) {
                certType = 'COMMON.AUTOMATIC_SIGN';
              } else if (userSession.trissUser && !userSession.automatic) {
                certType = 'COMMON.REMOTE_SIGN';
              }
              break;
            default:
              certType = '';
          }

          if (userSession.eseal) {
            certType = 'COMMON.ESEAL';
          }

          // Utilizzo del nuovo servizio di localizzazione delle date
          const expirationDate = this.dateLocalizationService.formatDisplayDate(
            certificate.validityTo.substring(0, 10)
          );

          const certState = this.translateService.instant(
            certificate.active ? 'COMMON.ACTIVE' : 'COMMON.INACTIVE'
          );

          this.isExpiring = this.dateLocalizationService.expiresWithinDays(
            certificate.validityTo.substring(0, 10),
            89
          );

          const daysDiff = this.dateLocalizationService.getDaysDifference(
            certificate.validityTo.substring(0, 10),
            moment()
          );
          console.log('expiration', daysDiff);
          const certTypeText = certType
            ? this.translateService.instant(certType)
            : '';
          this.primaryCard = {
            id: 0,
            onClick: () => this.go('/homepage'),
            title: fullName,
            statusIcon: certificate.active
              ? this.isExpiring
                ? 'warning'
                : 'success'
              : 'revoked',
            spidIcon: 'group',
            spidType: certTypeText
              ? `${certTypeText}\n${certState} · ${expirationDate}`
              : `${certState} · ${expirationDate}`,
            email: alias,
            selected: true,
          };

          this.profiles[0] = this.primaryCard;
        }
      });
  }
}
